import {
  isAreaId,
  isAccountId,
  isCalendarEventId,
  isCompanyId,
  isQuizId,
  isTaskId,
  isTeamId,
  isUserId,
  PlanPeriodValueSchema,
  type PlanKey,
} from '@terros/common'
import type { QueryKey } from '@tanstack/react-query'
import {
  accountKey,
  areaCanvassKey,
  areaGetKey,
  areaListInfiniteKey,
  eventKey,
  messageUnreadsKey,
  planKey,
  plansKey,
  profileKey,
  quizKey,
  quizListKey,
  quizTakeKey,
  taskKey,
  taskListKey,
  teamDownlineKey,
  teamKey,
  teamListKey,
  threadKey,
  threadListKey,
  tinyTeamKey,
  tinyUserKey,
  userKey,
  userListKey,
} from '@front/model'

type QueryDomain =
  | 'area'
  | 'user'
  | 'team'
  | 'account'
  | 'calendar'
  | 'task'
  | 'teamMember'
  | 'plan'
  | 'thread'
  | 'quiz'
  | 'quizUser'

export type TopicInvalidation = {
  topicId: string
  documentId: string
}

const ENTITY_TO_DOMAIN: Record<string, QueryDomain> = {
  area: 'area',
  user: 'user',
  team: 'team',
  teamMember: 'teamMember',
  account: 'account',
  event: 'calendar',
  task: 'task',
  plan: 'plan',
  thread: 'thread',
  quiz: 'quiz',
  quizUser: 'quizUser',
}

function parseEntity(topicId: string): string | undefined {
  const colonIndex = topicId.lastIndexOf(':')
  if (colonIndex === -1) return undefined
  return topicId.slice(colonIndex + 1)
}

function isThreadScopedUserTopic(topicId: string, entity: string): boolean {
  return entity === 'user' && topicId.startsWith('thread(')
}

function resolveAreaQueryKeys(documentId: string): QueryKey[] {
  const queryKeys: QueryKey[] = [areaListInfiniteKey(), areaCanvassKey()]
  if (!isAreaId(documentId)) return queryKeys

  return [areaGetKey(documentId), ...queryKeys]
}

function resolveUserQueryKeys(documentId: string): QueryKey[] {
  const queryKeys: QueryKey[] = [userListKey()]
  if (!isUserId(documentId)) return queryKeys

  return [userKey(documentId), profileKey(documentId), tinyUserKey(documentId), ...queryKeys]
}

function resolveTeamQueryKeys(documentId: string): QueryKey[] {
  const queryKeys: QueryKey[] = [teamListKey(), ['team-downline']]
  if (!isTeamId(documentId)) return queryKeys

  return [teamKey(documentId), tinyTeamKey(documentId), teamDownlineKey(documentId), ...queryKeys]
}

function resolveAccountQueryKeys(documentId: string): QueryKey[] {
  const queryKeys: QueryKey[] = [['accounts']]
  if (!isAccountId(documentId)) return queryKeys

  return [accountKey(documentId), ...queryKeys]
}

function resolveCalendarQueryKeys(documentId: string): QueryKey[] {
  const queryKeys: QueryKey[] = [['calendar-events'], ['calendar-events-infinite']]
  if (!isCalendarEventId(documentId)) return queryKeys

  return [eventKey(documentId), ...queryKeys]
}

function resolveTaskQueryKeys(documentId: string): QueryKey[] {
  const queryKeys: QueryKey[] = [taskListKey()]
  if (!isTaskId(documentId)) return queryKeys

  return [taskKey(documentId), ...queryKeys]
}

function resolveThreadQueryKeys(documentId: string): QueryKey[] {
  const queryKeys: QueryKey[] = [threadListKey(), messageUnreadsKey()]
  if (!isUserId(documentId) && !isTeamId(documentId) && !isCompanyId(documentId)) return queryKeys

  return [threadKey(documentId), ...queryKeys]
}

function resolveThreadPresenceQueryKeys(documentId: string): QueryKey[] {
  const queryKeys: QueryKey[] = [threadListKey()]
  if (!isUserId(documentId)) return queryKeys

  return [threadKey(documentId), ...queryKeys]
}

function resolvePlanQueryKeys(documentId: string): QueryKey[] {
  const queryKeys: QueryKey[] = [plansKey()]
  const key = parsePlanDocumentId(documentId)
  if (!key) return queryKeys

  return [planKey(1, key), planKey(2, key), ...queryKeys]
}

function resolveQuizQueryKeys(documentId: string): QueryKey[] {
  const queryKeys: QueryKey[] = [quizListKey()]
  if (!isQuizId(documentId)) return queryKeys

  return [quizKey(documentId), quizTakeKey(documentId), ...queryKeys]
}

function resolveQuizUserQueryKeys(documentId: string): QueryKey[] {
  if (!isQuizId(documentId)) return []

  return [quizTakeKey(documentId)]
}

function parsePlanDocumentId(documentId: string): PlanKey | undefined {
  const separatorIndex = documentId.indexOf('|')
  if (separatorIndex === -1) return

  const scopeId = documentId.slice(0, separatorIndex)
  if (!isUserId(scopeId) && !isTeamId(scopeId)) return

  const period = PlanPeriodValueSchema.safeParse(documentId.slice(separatorIndex + 1))
  if (!period.success) return

  return { scopeId, period: period.data }
}

export function resolveTopicToQueryKeys({ topicId, documentId }: TopicInvalidation): QueryKey[] {
  const entity = parseEntity(topicId)
  if (!entity) return []

  if (isThreadScopedUserTopic(topicId, entity)) return resolveThreadPresenceQueryKeys(documentId)

  const domain = ENTITY_TO_DOMAIN[entity]
  if (!domain) return []

  switch (domain) {
    case 'area':
      return resolveAreaQueryKeys(documentId)
    case 'user':
      return resolveUserQueryKeys(documentId)
    case 'team':
      return resolveTeamQueryKeys(documentId)
    case 'account':
      return resolveAccountQueryKeys(documentId)
    case 'teamMember':
      // Team membership changes invalidate the team websocket topic in the backend.
      return []
    case 'calendar':
      return resolveCalendarQueryKeys(documentId)
    case 'task':
      return resolveTaskQueryKeys(documentId)
    case 'plan':
      return resolvePlanQueryKeys(documentId)
    case 'thread':
      return resolveThreadQueryKeys(documentId)
    case 'quiz':
      return resolveQuizQueryKeys(documentId)
    case 'quizUser':
      return resolveQuizUserQueryKeys(documentId)
  }
}
